import { useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { SuccessDialog } from '@bliks-io/ui';
import type { ListTasksResponse, SurveyWithVariantCheck } from '@bliks-io/contracts';
import { countTasks } from '@/api/queries/useTasks';
import { useCelebrationStore } from '@/stores/celebration-store';

interface SurveyFinishedCardOptions {
  openSurveyId: string | null;
  selectSurvey: Dispatch<SetStateAction<string | null>>;
}

interface SurveyFinishedCard {
  /** Soonest due first. */
  remainingSurveyIds: string[];
}

// `dueDate` is optional; `sort` is stable, so ties keep the server's order.
function byDueDateUndatedLast(a: SurveyWithVariantCheck, b: SurveyWithVariantCheck) {
  if (a.dueDate === undefined) return b.dueDate === undefined ? 0 : 1;
  if (b.dueDate === undefined) return -1;
  return a.dueDate - b.dueDate;
}

/**
 * The card after a survey is finished on My Tasks. The counts come off the
 * task list as it stood when the answer was sent: the mutation drops the row
 * from the cache only once it resolves.
 */
export function useSurveyFinishedCard({ openSurveyId, selectSurvey }: SurveyFinishedCardOptions) {
  const { t } = useTranslation();
  const recordCompletion = useCelebrationStore((state) => state.recordCompletion);
  // Read when an answer lands, which can be after its survey was closed.
  const currentOpenSurveyId = useRef(openSurveyId);
  currentOpenSurveyId.current = openSurveyId;
  const [card, setCard] = useState<SurveyFinishedCard | null>(null);
  // The last task gets no card, and the page's banner is no live region, so
  // without this a screen reader would hear nothing at all.
  const [announcement, setAnnouncement] = useState('');
  // A live region only speaks when its text changes, so the next thanks needs
  // this one gone: it clears once another survey is opened.
  if (openSurveyId !== null && announcement !== '') setAnnouncement('');

  const showCardFor = (surveyId: string, tasks: ListTasksResponse | undefined) => {
    const remainingSurveyIds = (tasks?.pendingSurveys ?? [])
      .filter((survey) => survey._id !== surveyId)
      .sort(byDueDateUndatedLast)
      .map((survey) => survey._id);
    // A deep-linked survey can be missing from a list cached before it existed.
    const openAfter =
      countTasks(tasks) - (tasks?.pendingSurveys.length ?? 0) + remainingSurveyIds.length;
    // No card without a task list, whose counts would be guesses, nor on the
    // last open task: the page itself celebrates that (banner and empty
    // state), and a card on top would say it twice.
    if (!tasks || openAfter <= 0) {
      setAnnouncement(t('celebration.survey.title'));
      return;
    }

    setCard({ remainingSurveyIds });
  };

  /**
   * Pass the task list read before the answer was sent, not after — or
   * `undefined` when none had loaded, which gets no card rather than a wait
   * on a fetch. Closes the survey in the same tick the card opens, so the two
   * never overlap. An answer that lands after the user closed its survey only
   * counts: the survey open by then, and what was typed into it, stay.
   */
  const celebrate = (surveyId: string, tasks: ListTasksResponse | undefined) => {
    recordCompletion();
    if (currentOpenSurveyId.current !== surveyId) return;
    selectSurvey((current) => (current === surveyId ? null : current));
    showCardFor(surveyId, tasks);
  };

  const close = () => setCard(null);
  const remainingSurveyIds = card?.remainingSurveyIds ?? [];
  const [nextSurveyId] = remainingSurveyIds;
  const actions = nextSurveyId
    ? {
        description: t('celebration.survey.remaining', { count: remainingSurveyIds.length }),
        primary: {
          label: t('celebration.survey.nextSurvey'),
          onClick: () => {
            close();
            selectSurvey(nextSurveyId);
          }
        },
        secondary: { label: t('celebration.later'), onClick: close }
      }
    : {
        description: t('celebration.survey.allDone'),
        primary: { label: t('common.Done'), onClick: close }
      };

  const element = (
    <>
      {/* Explicit aria-live: Radix's hideOthers spares only [aria-live], not role="status". */}
      <p role="status" aria-live="polite" className="sr-only">
        {announcement}
      </p>
      {card && (
        <SuccessDialog open title={t('celebration.survey.title')} {...actions} onDismiss={close} />
      )}
    </>
  );

  return { celebrate, element };
}
